import {
  Box,
  Dropdown,
  MixpanelEventSource,
  MixpanelSourceProvider,
  Stack,
  useIsMobile,
  type DropdownProps,
} from '@talos/kyoko';
import { useEffect, useRef, type ReactNode } from 'react';
import { MarketSelectorBlotter } from './blotter/MarketSelectorBlotter';
import { useMarketSelectorContext } from './MarketSelectorContext';
import { MarketSelectorWindow } from './MarketSelectorWindow';
import { MarketSelectorPresetsList } from './presets/MarketSelectorPresetsList';
import { NARROW_MARKET_SELECTOR_PANEL_MAX_WIDTH } from './tokens';

export type MarketSelectorPanelProps = {
  dropdownProps: Omit<DropdownProps, 'children'>;
  /** Useful if you have multiple market selectors rendered at once to give them different testIDs. */
  testID?: string;
  /** Pass a react node here to render above the blotter */
  panelTopPrefix?: ReactNode;
  /** Pass a react node here to render below the blotter */
  panelBottomSuffix?: ReactNode;
  panelContentKey?: string;
  /** Set to true to start with the blotter "page" open in the narrow market selector panel */
  showBlotterOnOpen?: boolean;
};

/**
 * Narrow version of the MarketSelectorPanel.
 * Preset list overlay the blotter in narrow mode.
 * Slide the preset list off screen to show the blotter.
 * */
export const NarrowMarketSelectorPanel = ({
  dropdownProps,
  testID,
  panelTopPrefix,
  panelBottomSuffix,
  panelContentKey,
  showBlotterOnOpen,
  panelMaxWidth = NARROW_MARKET_SELECTOR_PANEL_MAX_WIDTH,
}: MarketSelectorPanelProps & { panelMaxWidth?: string }) => {
  const initRef = useRef(false);
  const { narrowModeConfig, advancedMode, disableAggregations } = useMarketSelectorContext();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!showBlotterOnOpen || initRef.current) {
      return;
    }
    initRef.current = true;
    narrowModeConfig.setShowMarketSelectorBlotter(true);
  }, [narrowModeConfig, showBlotterOnOpen]);

  return advancedMode && !isMobile ? (
    <MarketSelectorWindow
      testID={testID}
      isOpen={dropdownProps.isOpen}
      referenceElement={dropdownProps.referenceElement}
      headerProps={dropdownProps.headerProps}
      panelTopPrefix={panelTopPrefix}
      panelBottomSuffix={panelBottomSuffix}
      panelContentKey={panelContentKey}
    />
  ) : (
    <Dropdown portalize isParentDropdown {...dropdownProps} dropdownWidth={panelMaxWidth}>
      <MixpanelSourceProvider value={MixpanelEventSource.MarketAccountSelector}>
        <Stack key={panelContentKey} h="100%" data-testid={testID ?? 'market-selector-panel'}>
          {panelTopPrefix && (
            <Box borderBottom="1px solid" borderColor="backgroundDivider">
              {panelTopPrefix}
            </Box>
          )}
          <Stack flex="1" position="relative">
            <MarketSelectorBlotter flex="1" />
            {!disableAggregations ? (
              <MarketSelectorPresetsList
                forceShowSearch={true}
                position="absolute"
                top={0}
                inset={0}
                zIndex={1}
                transform={narrowModeConfig.showMarketSelectorBlotter ? 'translateX(-100%)' : undefined}
                transition="transform 200ms ease"
              />
            ) : null}
          </Stack>
          {panelBottomSuffix && (
            <Box borderTop="1px solid" borderColor="backgroundDivider">
              {panelBottomSuffix}
            </Box>
          )}
        </Stack>
      </MixpanelSourceProvider>
    </Dropdown>
  );
};
